import {LoadingOutlined, PlayCircleOutlined} from '@ant-design/icons';
import {Button, Col, Drawer, Dropdown, Menu, message, Popconfirm, Popover, Row, Space, Tag, Tooltip} from "antd";
import {FC, useState} from "react";
import {useHistory} from "react-router";
import appConfig from "../../../appConfig";
import Icon from "../../../assets/icons/icon";
import EstimaticOut from '../../../assets/images/estimatic-out.png';
import EstimaticPlus from '../../../assets/images/estimatic-plus.png';
import ActiveUsers from "../../../components/activeUsers";
import ActivityDrawer from "../../../components/activityDrawer";
import Checklist from "../../../components/checklist/checklist";
import DividerLine from '../../../components/lineSplit';
import {renderErrorMessage} from '../../../components/messages/errorMessage';
import openModal from "../../../components/modal/openModal";
import PaymentCardDetails from '../../../components/paymentDetails/PaymentCardDetails';
import TaxFlagsSwitches from "../../../components/taxFlagsSwitches";
import TaxCaseStore from "../../../contexts/taxCaseStore";
import UserStore from "../../../contexts/userStore";
import _t from "../../../lang/translate";
import {getPath} from "../../../routes/appRoutes";
import log from "../../../services/logService";
import {createEstimaticOfTaxCase, createImportOfTaxCase,} from "../../../services/taxService";
import {IdType, LocationStateType, Person} from "../../../types/appTypes";
import format from "../../../utilities/formatNumbers";
import {isAxiosError} from "../../../utilities/typeGuard";
import TaxCaseDealer from "./admin/taxCaseDealer";
import TaxAttachments from './taxAttachments';
import TaxDownloadInvoiceBtn from './user/taxDownloadInvoiceBtn';

const jumpToWidgetCaseUrlBase = process.env.REACT_APP_WIDGET_JUMP_URL || '';

const LEGACY_SITE_URL = process.env.REACT_APP_LEGACY_SITE_URL
const getLegacySiteCaseLink = (taxCaseId: IdType) => {
  const lastChar = LEGACY_SITE_URL?.charAt(LEGACY_SITE_URL.length - 1);
  if (lastChar === "/") {
    return `${LEGACY_SITE_URL}tax/${taxCaseId}`;
  } else {
    return `${LEGACY_SITE_URL}/tax/${taxCaseId}`;
  }
}
const getLegacySiteLoginLink = () => {
  const lastChar = LEGACY_SITE_URL?.charAt(LEGACY_SITE_URL.length - 1);
  if (lastChar === "/") {
    return `${LEGACY_SITE_URL}login-admin`;
  } else {
    return `${LEGACY_SITE_URL}/login-admin`;
  }
}

const TAX_SYSTEM_MIGRATION_ID_CUTOFF = process.env.REACT_APP_TAX_SYSTEM_MIGRATION_ID_CUTOFF
const TaxCaseHeadExtra: FC = () => {
  const history = useHistory<LocationStateType>();
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer();
  const { hasPermission } = UserStore.useContainer();
  const [drawerVisibility, setDrawerVisibility] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingEstimatic, setLoadingEstimatic] = useState(false)
  const [loadingAutoOffer, setLoadingAutoOffer] = useState(false)
  const [docsVisible, setDocsVisible] = useState(false)
  const legacyCaseLink = getLegacySiteCaseLink(taxCase.id)
  const legacyLoginLink = getLegacySiteLoginLink()

  const shouldShowLegacyLink = TAX_SYSTEM_MIGRATION_ID_CUTOFF && taxCase.id < parseInt(TAX_SYSTEM_MIGRATION_ID_CUTOFF); // TODO: Define this later
  const isAdmin = hasPermission("tax.isAdmin");
  const canCreateImport = hasPermission("import.create");
  const canCreateLeasing = hasPermission("leasing.create");

  const handleCreateLeasing = async () => {
    try {
      const { data } = await openModal<{ data: any }>("taxToLeasingCase", {
        id: taxCase.id,
        vehicleTypeId: taxCase.vehicle?.typeId,
        from: "tax",
      });
      if (data)
        history.push({
          pathname: getPath("leasing", "new"),
          state: { from: { pathname: "tax" }, data },
        });
    } catch (e) {
    }
  };

  const handleCreateImport = async () => {
    try {
      const { data } = await createImportOfTaxCase(taxCase.id);
      if (data?.id) {
        history.push({
          pathname: getPath("import", `${data.id}`),
        });
      } else {
        log("Exporting tax to Import case returned data without Id", { data });
      }
    } catch (error) {
      const errorMessage = isAxiosError(error) ? error.response?.data?.message : null;
      message.error(errorMessage || _t("msg.unknown_error"));
    }
  };

  const handleCreateOpenEstimatic = async (forceCreate = false) => {
    const link = taxCase.links?.estimatic;

    if (!forceCreate && link) {
      window.open(link, "_blank");
      return true;
    }

    try {
      setLoadingEstimatic(true)
      const { data } = await createEstimaticOfTaxCase(taxCase.id);
      setTaxCase(data);

      message.success(_t("msg.process_started"));
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingEstimatic(false)
    }
  };

  async function startAttemptAutoOffer() {
    try {
      setLoadingAutoOffer(true)
      await openModal("attemptTaxAutoOffer", { id: taxCase.id });
      message.success(_t("msg.process_started"));
      history.replace(getPath('tax'))
    } catch (error) {
      if (error !== "Cancel") {
        renderErrorMessage(error)
      }
    } finally {
      setLoadingAutoOffer(false)
    }
  }

  const actionsMenu = (
    <Menu>
      <Menu.ItemGroup title="Åbn sag i">
        <Menu.Item key="toEstimatic" onClick={() => handleCreateOpenEstimatic()}
          disabled={taxCase.flags.generatingEstimaticCase || !taxCase.links?.estimatic}
        >
          {_t("estimatic")}
          {taxCase.flags.generatingEstimaticCase ? (<LoadingOutlined className="ml-1" />) : null}
        </Menu.Item>
        {taxCase.links?.dealerView && (
          <Menu.Item key="dmfPro">
            <a href={taxCase.links.dealerView} target="_blank">
              DMF PRO
            </a>
          </Menu.Item>
        )}
        {taxCase.caseSourceId === appConfig("widgetCaseSourceId") && (
          <Menu.Item key="toWidget">
            <a href={`${jumpToWidgetCaseUrlBase}/${taxCase.id}`} target="_blank">
              WIDGET
            </a>
          </Menu.Item>
        )}
      </Menu.ItemGroup>
      <Menu.ItemGroup title="Opret sag">
        <Popconfirm onConfirm={() => handleCreateOpenEstimatic(true)} title={_t("msg.confirm_action")}>
          <Menu.Item key="toEstimatic"
            disabled={taxCase.flags.generatingEstimaticCase}
          >
            {_t("estimatic")}
            {taxCase.flags.generatingEstimaticCase ? (<LoadingOutlined className="ml-1" />) : null}
            <Icon name="coin-line" className='ml-1' />
          </Menu.Item>
        </Popconfirm>
        {canCreateLeasing && (
          <Menu.Item key="toLeasing" onClick={handleCreateLeasing}>
            {_t("leasing")}
          </Menu.Item>
        )}
        {canCreateImport && (
          <Menu.Item key="toImport" onClick={handleCreateImport}>
            {_t("importCase")}
          </Menu.Item>
        )}

      </Menu.ItemGroup>
    </Menu>
  )

  return (
    <div>
      <Row align="bottom" gutter={[8, 16]}>
        <Col flex="auto">{isAdmin && <TaxCaseDealer />}</Col>
        <Col>
          <Space wrap>
            <ActiveUsers context="tax" />
            {shouldShowLegacyLink && (
              <Popover title={"Åbn sagen i legacy"} content={
                <>
                  Bliver du omstillet til DMF-PRO? {" "}
                  <a href={legacyLoginLink} target='_blank'>Login admin</a>
                </>
              }>
                <Button href={legacyCaseLink}
                  target='_blank'
                  style={{ padding: 0, width: 31 }}
                ><Icon name="terminal" /></Button>
              </Popover>
            )}
            <Button onClick={() => setDocsVisible(true)}
              icon={taxCase.files?.length ? (
                <Icon name="FolderStar" size='large' />
              ) : (
                <Icon name="folder" />
              )}
            />
            {!!taxCase.links?.estimatic ? (
              <Tooltip title={_t("open", "estimatic")}>
                <Button onClick={() => handleCreateOpenEstimatic()}
                  loading={taxCase.flags.generatingEstimaticCase || loadingEstimatic}
                  icon={<img height={24} width={24}
                    src={EstimaticOut} />}
                />
              </Tooltip>
            ) : (
              <Popconfirm onConfirm={() => handleCreateOpenEstimatic()} title={_t("msg.confirm_action")} >
                <Tooltip title={`${_t("msg.create_estimatic_case")} (koster ${appConfig("estimaticCasePrice")} kr.)`}>
                  <Button
                    loading={taxCase.flags.generatingEstimaticCase || loadingEstimatic}
                    icon={<img height={24} width={24} src={EstimaticPlus} />}
                  />
                </Tooltip>
              </Popconfirm>
            )}


            <Drawer width={500}
              onClose={() => setDocsVisible(false)}
              visible={docsVisible}
              bodyStyle={{ padding: 0 }}
            >
              <TaxAttachments />
            </Drawer>
            {hasPermission("tax.checklist") && (
              <Checklist
                {...{
                  isAdmin,
                  id: taxCase.id,
                  context: "tax",
                  btnText: _t("checklist"),
                  asDrawer: true,
                  useIconOnly: true
                }}
              />
            )}
            <Tooltip title={"Generer automatisk tilbud"} placement='bottom'>
              <Button onClick={startAttemptAutoOffer} loading={loadingAutoOffer} className='text-primary' icon={<PlayCircleOutlined />} />
            </Tooltip>
            <Dropdown overlay={actionsMenu}>
              <Button>{_t("actions")}<Icon style={{ fontSize: 16, verticalAlign: "2px" }} name="chevron-down-outline" /></Button>
            </Dropdown>
          </Space>
        </Col>
      </Row>
      <DividerLine fluid style={{ marginBottom: 12, marginTop: 16 }} />
      <Row align="middle" gutter={[8, 16]} className='pb-03'>
        <Col flex="auto">
          <Tag className="ant-tag-exception">
            <Icon name="coin-line" className='mr-05' />
            {format.integer(taxCase.totalExternalCosts)}
          </Tag>
        </Col>
        <Col>
          <Space size={"middle"}>
            <span>{_t("activity")}:</span>
            <TaxFlagsSwitches setLoading={setLoading} />
            <Button icon={<Icon name="clock-clockwise" />}
              onClick={() => setDrawerVisibility(true)}
            />
            <ActivityDrawer
              visible={drawerVisibility}
              isAdmin={isAdmin}
              setVisibility={setDrawerVisibility}
              type="tax"
              id={taxCase.id}
              sendingDisabledReason={!taxCase.flags.partnerCase ? null : _t('externalCaseContactCustomerManually')}
              extra={

                <Space direction="vertical" style={{ width: "100%" }}>
                  <LogCard date={taxCase.log?.accepted?.date}
                    label={_t("case", "accepted")}
                    checked={taxCase.flags.accepted}
                    person={taxCase.log?.accepted?.person}
                  />
                  <LogCard date={taxCase.log?.uploaded?.date}
                    label={_t("documents", "received")}
                    checked={taxCase.flags.uploaded}
                    person={taxCase.log?.uploaded?.person}
                  />
                  <LogCard date={taxCase.log?.paid?.date}
                    label={_t("payment", "received")}
                    checked={taxCase.flags.paid}
                    person={taxCase.log?.paid?.person}
                  />
                  {taxCase.flags.paid && (
                    <PaymentCardDetails
                      paymentDate={taxCase.log?.paid?.date}
                      paymentPerson={taxCase.log?.paid?.person.fullName}
                      paymentId={taxCase.paymentDetails?.paymentId}
                      footer={
                        <div className="d-flex justify-content-end mt-1">
                          <TaxDownloadInvoiceBtn>
                            {_t("receipt")}
                          </TaxDownloadInvoiceBtn>
                        </div>
                      }
                    />
                  )}
                </Space>
              }
            />
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default TaxCaseHeadExtra;

type LogCardProps = {
  date?: string;
  person: Person | undefined;
  label: string;
  checked?: boolean;
}

function LogCard({ date, person, checked, label }: LogCardProps) {
  return (
    <Row className="border-light border-rounded" style={{ paddingBlock: 12, paddingInline: 4 }} align="middle" gutter={[16, 16]}>
      <Col flex="0">
        <div style={{
          height: 31,
          width: 31,
          background: checked ? "#D2F4E2" : "#EFF3F5",
          color: checked ? "#1EC670" : "#95A5AE",
          borderRadius: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
          <Icon size="medium" name={checked ? "checkmark-circle-outline" : "radio-button-off-outline"} />
        </div>
      </Col>
      <Col flex="auto">
        <p className="m-0 text-bold text-smallest">{label}</p>
        {person && (
          <p className="m-0 muted text-smallest">{person?.fullName}, {person?.email}</p>
        )}
        {date && <p className="m-0 muted text-smallest">{format.dateAndTime(date)}</p>}
      </Col>
    </Row>
  )
}
